// Simple memoization for processed images
const processedImageMemo = new Map<string, string>();

export const processImage = ({
  imageSrc,
  settings: { whitePoint = 240, blackPoint = 0, contrast = 4, gamma = 0.8 } = {}
}) => {
  // Create cache key based on image source and settings
  const cacheKey = `${imageSrc}-${whitePoint}-${blackPoint}-${contrast}-${gamma}`;

  // Check if we have a cached result
  if (processedImageMemo.has(cacheKey)) {
    return Promise.resolve(processedImageMemo.get(cacheKey)!);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    // Handle CORS
    if (imageSrc?.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas') as HTMLCanvasElement;
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Process each pixel
        for (let i = 0; i < data.length; i += 4) {
          // Convert to grayscale using luminance formula
          const luminance = 0.299 * data[`${i}`] + 0.587 * data[`${i + 1}`] + 0.114 * data[`${i + 2}`];

          let value = luminance;

          // Apply black and white point adjustment
          if (value <= blackPoint) {
            value = 0;
          } else if (value >= whitePoint) {
            value = 255;
          } else {
            // Normalize to 0-1 range
            value = (value - blackPoint) / (whitePoint - blackPoint);

            // Apply contrast (using a curve)
            value = (value - 0.5) * contrast + 0.5;
            value = Math.max(0, Math.min(1, value));

            // Apply gamma correction
            value = Math.pow(value, 1 / gamma);

            // Scale back to 0-255
            value = value * 255;
          }

          // Set RGB to same value for grayscale
          data[`${i}`] = value; // R
          data[`${i + 1}`] = value; // G
          data[`${i + 2}`] = value; // B
          // Alpha remains unchanged
        }

        // Put processed image data back
        ctx.putImageData(imageData, 0, 0);

        // Convert to data URL
        canvas.toBlob((blob) => {
          if (blob) {
            const objectURL = URL.createObjectURL(blob);
            // Cache the result
            processedImageMemo.set(cacheKey, objectURL);
            resolve(objectURL);
          } else {
            reject(new Error('Failed to create blob'));
          }
        });
      } catch (error) {
        reject(error);
      }
    };

    img.src = imageSrc;
  });
};
